import { t } from '@lingui/core/macro';
import { z } from 'zod';

import { withFieldGroup } from '@/components/Form';
import { FieldError } from '@/components/Form/fields';
import { isURLPathStartsWithProtocol, isURLPathStartsWithSlash } from '@/helper/validator';
import { cn } from '@/lib/utils';

import RuleBuilderOperator from './RuleBuilderOperator';
import RuleBuilderText from './RuleBuilderText';
import RuleBuilderValueInput from './RuleBuilderValueInput';
import type { AttributeFieldValues, RuleEntityAttributeType } from './types';

const MAX_VALUES = 100;

const buildValueSubmitSchema = (attributeId: string, operator?: string) => {
  return z
    .unknown()
    .refine((val) => (Array.isArray(val) ? val.length > 0 : val != null), t`Please select a value`)
    .refine(
      (val) => (Array.isArray(val) ? val.length <= MAX_VALUES : true),
      t`You can select a maximum of ${MAX_VALUES} values`,
    )
    .superRefine((val, ctx) => {
      if (attributeId === 'url' && (operator === 'eq' || operator === 'ne')) {
        const values = Array.isArray(val) ? val : [val];
        if (values.some((item) => isURLPathStartsWithSlash(item as string))) {
          ctx.addIssue({
            code: 'custom',
            message: t`URL paths must not start with a "/" character. Valid paths are: "careers", "products/1", and "example?page=1".`,
          });
        }

        if (values.some((item) => isURLPathStartsWithProtocol(item as string))) {
          ctx.addIssue({
            code: 'custom',
            message: t`URL paths should not contain domains (e.g. http://example.com). Please enter a valid URL path.`,
          });
        }
      }
    });
};

const RuleBuilderAttribute = withFieldGroup({
  defaultValues: {
    operator: 'eq',
    value: undefined,
  } as AttributeFieldValues,
  props: {
    attribute: undefined as unknown as RuleEntityAttributeType,
    disabled: false as boolean,
  },
  render: function RuleBuilderAttributeRender({ group, attribute, disabled = false }) {
    const operatorSchema = z.string().min(1, t`Please select an operator`);
    return (
      <div
        className={cn(
          'flex w-full items-start justify-start gap-6',
          'max-xl:gap-x-4.5 max-xl:gap-y-2',
          'max-lg:flex-col max-lg:items-stretch max-lg:gap-x-3 max-lg:gap-y-1.5',
        )}
      >
        <div className="flex shrink-0 flex-nowrap items-center gap-6 max-lg:shrink">
          <div className="flex flex-nowrap items-center gap-2">
            {attribute.icon}
            <RuleBuilderText className="whitespace-nowrap" variant="body1">
              {attribute.name}
            </RuleBuilderText>
          </div>
          <group.AppField
            name="operator"
            validators={{
              onDynamic: ({ value }) => operatorSchema.safeParse(value).error?.issues,
            }}
          >
            {(field) => (
              <RuleBuilderOperator
                attribute={attribute}
                value={field.state.value}
                onChange={(val) => field.handleChange(val ?? ('eq' as typeof field.state.value))}
                onBlur={field.handleBlur}
                errors={
                  field.state.meta.isTouched
                    ? field.state.meta.errors.map((e) =>
                        typeof e === 'string' ? { message: e } : e,
                      )
                    : []
                }
                disabled={disabled}
                className="max-w-75 flex-1 max-xl:max-lg:w-50 xl:w-75"
              />
            )}
          </group.AppField>
        </div>
        <div className="flex-1 max-lg:w-full max-lg:flex-none">
          <group.AppField
            name="value"
            validators={{
              onDynamic: ({ value }) => {
                const operator = group.getFieldValue('operator');
                const schema = buildValueSubmitSchema(attribute.id, operator);
                const result = schema.safeParse(value);
                return result.error?.issues ? result.error.issues : undefined;
              },
            }}
          >
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <>
                  <RuleBuilderValueInput
                    aria-invalid={isInvalid}
                    attribute={attribute}
                    disabled={disabled}
                    value={field.state.value}
                    onChange={(val) => field.handleChange(val ?? undefined)}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </>
              );
            }}
          </group.AppField>
        </div>
      </div>
    );
  },
});

export default RuleBuilderAttribute;
